import { useEffect, useState, type RefObject } from "react";
import { useLocation } from "react-router-dom";

import { PAGE_META, type PublicPageKey } from "./content";

/** Título y descripción de cada página pública; restaura los anteriores al
 * salir (la app interna tiene los suyos). */
export function usePageMeta(page: PublicPageKey): void {
  useEffect(() => {
    const meta = PAGE_META[page];
    const previousTitle = document.title;
    let tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const created = !tag;
    if (!tag) {
      tag = document.createElement("meta");
      tag.name = "description";
      document.head.appendChild(tag);
    }
    const previousDescription = tag.content;
    document.title = meta.title;
    tag.content = meta.description;
    return () => {
      document.title = previousTitle;
      if (!tag) return;
      if (created) tag.remove();
      else tag.content = previousDescription;
    };
  }, [page]);
}

/** true mientras el elemento está (parcialmente) en pantalla. Sin
 * IntersectionObserver (navegadores viejos) se considera visible. */
export function useInView<T extends Element>(ref: RefObject<T>, rootMargin = "0px"): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), { rootMargin });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, rootMargin]);
  return inView;
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Re-desplazamientos después del primero: el contenido de arriba del ancla
 * todavía puede crecer (la prueba en vivo llega con el directorio, las
 * maquetas y las fuentes terminan de pintar). */
const HASH_RESCROLL_DELAYS_MS = [300, 900];
/** Después de esto la página se da por asentada y ya no se corrige nada
 * (holgado: en una red lenta el directorio tarda en llegar). */
const HASH_SETTLE_MS = 6000;
/** Cualquiera de estos gestos significa que la persona ya se está moviendo
 * por su cuenta: a partir de ahí no se le vuelve a mover la página. */
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

/** Al llegar con #ancla (p. ej. /organizador#demo desde otra página, o una
 * píldora del recorrido), baja a esa sección y la vuelve a alinear mientras la
 * página se asienta. Sin esto, los navegadores sin «scroll anchoring» (Safari
 * de iPhone, el navegador de WhatsApp) se quedan cortos cuando algo de arriba
 * crece después del primer salto. Deja de corregir en cuanto la persona toca,
 * rueda, usa el teclado o desplaza la página por otro medio (barra de
 * desplazamiento). `rootRef` es el contenedor de la página: si cambia de alto
 * durante el asentamiento, se vuelve a alinear. */
export function useHashScroll(rootRef?: RefObject<HTMLElement>): void {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    // Un #fragmento mal codificado (p. ej. un enlace de campaña «#50%descuento»)
    // no debe tumbar la página: se usa tal cual.
    let id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch {
      // se queda el texto crudo
    }
    let stopped = false;
    let frame = 0;
    const timers: number[] = [];
    let observer: ResizeObserver | undefined;
    /** Dónde dejamos la página en la última alineación (NaN: aún no). */
    let alignedY = Number.NaN;

    // Sin animación: el desplazamiento suave calcula la distancia una sola vez
    // y se queda corto si la página crece mientras anima.
    const align = () => {
      if (stopped) return;
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView?.({ behavior: "auto", block: "start" });
      alignedY = window.scrollY;
    };
    const alignNextFrame = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(align);
    };
    // Un desplazamiento que no hicimos nosotros (barra de desplazamiento,
    // buscar en la página…) también cuenta como gesto de la persona. Con
    // «scroll anchoring» el navegador también mueve la página al crecer el
    // contenido; parar ahí no estorba porque ese navegador ya sostiene el ancla.
    const onScroll = () => {
      if (!Number.isNaN(alignedY) && Math.abs(window.scrollY - alignedY) > 2) stop();
    };
    const stop = () => {
      if (stopped) return;
      stopped = true;
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
      observer?.disconnect();
      USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop, true));
      window.removeEventListener("scroll", onScroll);
    };

    USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
    window.addEventListener("scroll", onScroll, { passive: true });
    alignNextFrame();
    HASH_RESCROLL_DELAYS_MS.forEach((delay) => timers.push(window.setTimeout(align, delay)));
    timers.push(window.setTimeout(stop, HASH_SETTLE_MS));
    const root = rootRef?.current;
    if (root && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(alignNextFrame);
      observer.observe(root);
    }
    return stop;
  }, [hash, pathname, rootRef]);
}
